import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';

/**
 * Language toggle pill — flips between EN ↔ ES.
 *
 * Lives in the player header so customers can switch instantly and the choice
 * sticks (persisted via i18next's LanguageDetector to localStorage).
 *
 * Renders as a compact pill: `🌐 EN | ES` — current language highlighted.
 */
const LanguageToggle = ({ compact = false }) => {
    const { i18n, t } = useTranslation();
    const current = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);

    const setLang = (lng) => {
        if (lng !== current) {
            i18n.changeLanguage(lng);
        }
    };

    return (
        <div
            className={`inline-flex items-center rounded-full border border-white/15 bg-[#121212] ${compact ? 'px-1.5 py-1 gap-1' : 'px-2 py-1 gap-1.5'}`}
            title={t('language.toggle_label')}
            data-testid="language-toggle"
        >
            <Globe className={`${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-[#A0AAB2]`} />
            {['en', 'es'].map((lng) => {
                const active = current === lng;
                return (
                    <button
                        key={lng}
                        type="button"
                        onClick={() => setLang(lng)}
                        className={`${compact ? 'text-[10px] px-1.5' : 'text-[11px] px-2'} font-black uppercase tracking-wider rounded-full transition-colors ${
                            active
                                ? 'bg-[#FFD700] text-black'
                                : 'text-[#A0AAB2] hover:text-white'
                        }`}
                        aria-pressed={active}
                        data-testid={`language-toggle-${lng}`}
                    >
                        {lng}
                    </button>
                );
            })}
        </div>
    );
};

export default LanguageToggle;
